/**
 * Returns a function, that, when invoked, will only be triggered at most once during
 * a given window of time.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Time window (in milliseconds).
 * @returns {Function}
 */
function throttle(fn, wait = 300) {
  let throttleTimeoutId = -1;
  let tick = false;

  return () => {
    clearTimeout(throttleTimeoutId);
    throttleTimeoutId = setTimeout(fn, wait);

    if (!tick) {
      fn.call();
      tick = true;
      setTimeout(() => {
        tick = false;
      }, wait);
    }
  };
}

if (!customElements.get('sticky-atc')) {
  customElements.whenDefined('product-form').then(() => {
    class StickyAtc extends customElements.get('product-form') {
      constructor() {
        super();

        this.section = this.closest('.section-main-product');
        this.mainProductForm = this.section.querySelector('.js-product');
        this.buyButtons = this.section.querySelector('buy-buttons');
        this.imageContainer = this.querySelector('.sticky-atc__image');
        this.variantContainer = this.querySelector('.sticky-atc__details__variant');
        this.variantTitle = this.querySelector('.sticky-atc__details__variant__title');
        this.variantTitle?.toggleAttribute('hidden', !this.section.querySelector('variant-picker'));

        this.alwaysVisible = this.classList.contains('sticky-atc--end');
        if (!this.alwaysVisible) {
          this.throttledOnScroll = throttle(StickyAtc.handleScroll.bind(this));
          window.addEventListener('scroll', this.throttledOnScroll);
        } else {
          this.classList.remove('sticky-atc--out');
          document.body.classList.remove('scrolled-to-bottom');
        }

        this.onStickyAtcClick = this.onStickyAtcClick.bind(this);
        this.addEventListener('click', this.onStickyAtcClick, true);

        this.section.addEventListener('on:variant:change', this.onVariantChange.bind(this));
        this.section.addEventListener('on:media-gallery:change', this.updateImage.bind(this));

        this.updateImage();
      }

      disconnectedCallback() {
        if (this.throttledOnScroll) {
          window.removeEventListener('scroll', this.throttledOnScroll);
        }
        this.removeEventListener('click', this.onStickyAtcClick, true);
      }

      onStickyAtcClick(evt) {
        const changeLink = evt.target.closest('.sticky-atc__details__variant__change');
        if (!changeLink) return;

        const href = changeLink.getAttribute('href');
        if (!href || href.charAt(0) !== '#') return;

        const target = this.section.querySelector(href) || document.querySelector(href);
        if (!target) return;

        evt.preventDefault();
        evt.stopPropagation();

        const top = target.getBoundingClientRect().top + window.scrollY - theme.stickyHeaderHeight() - 20;
        window.scrollTo({ top, left: 0, behavior: 'smooth' });
      }

      /**
       * Handles submission of the product form.
       * @param {object} evt - Event object.
       */
      async handleSubmit(evt) {
        evt.preventDefault();

        const variantId = this.section.querySelector('product-form buy-buttons input[name="id"]').value;
        if (!variantId) {
          const target = this.section.querySelector('#product-info');
          if (target && target.offsetParent) {
            evt.preventDefault();
            theme.scrollToRevealElement(target);
          }
          return;
        }

        // Validate main form
        const customFormValid = this.mainProductForm.validate();
        if (!customFormValid || !this.mainProductForm.form.reportValidity()) {
          evt.preventDefault();
          if (!customFormValid) {
            theme.scrollToRevealElement(this.mainProductForm);
          } else {
            const input = Array.from(this.mainProductForm.form.elements).find(
              (el) => !el.checkValidity()
            );
            setTimeout(() => theme.scrollToRevealElement(input), 100);
          }
          return;
        }

        const accessoryButtons = this.section.querySelectorAll('.accessory-toggle-btn');
        const mainVariantId = this.form.querySelector('input[name="id"]')?.value || variantId;
        const mainQtyInput = this.mainProductForm.form.querySelector('input[name="quantity"]');
        const mainQty = mainQtyInput ? parseInt(mainQtyInput.value, 10) : 1;
        const normalizedMainQty = Number.isFinite(mainQty) && mainQty > 0 ? mainQty : 1;

        const selectedAccessoryVariantIds = Array.from(accessoryButtons || [])
          .filter((btn) => btn.classList.contains('remove'))
          .filter((btn) => !btn.disabled && btn.dataset.soldOut !== 'true')
          .map((btn) => btn.dataset.variantId)
          .filter((id) => id && id !== mainVariantId);

        const combinedAccessoryVariantIds = selectedAccessoryVariantIds
          .filter((id) => id && id !== mainVariantId)
          .filter((id, idx, arr) => arr.indexOf(id) === idx);

        const idInput = this.form.querySelector('input[name="id"]');
        this.form.querySelectorAll('[data-copied], [data-accessory-injected]').forEach((el) => el.remove());

        if (combinedAccessoryVariantIds.length) {
          if (idInput) idInput.disabled = true;

          const injectHidden = (name, value) => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = name;
            input.value = String(value);
            input.dataset.accessoryInjected = 'true';
            this.form.appendChild(input);
          };

          injectHidden('items[0][id]', mainVariantId);
          injectHidden('items[0][quantity]', normalizedMainQty);

          combinedAccessoryVariantIds.forEach((accessoryVariantId, idx) => {
            const itemIndex = idx + 1;
            injectHidden(`items[${itemIndex}][id]`, accessoryVariantId);
            injectHidden(`items[${itemIndex}][quantity]`, 1);
            injectHidden(`items[${itemIndex}][properties][Accessory]`, 'true');
            injectHidden(`items[${itemIndex}][properties][Main_Product]`, mainVariantId);
          });
        } else if (idInput) {
          idInput.disabled = false;
        }

        const formData = new FormData(this.mainProductForm.form);
        for (const p of formData) {
          if (p[0] === 'id') continue;
          if (p[0].startsWith('items[')) continue;
          if (combinedAccessoryVariantIds.length && p[0] === 'quantity') continue;
          const input = document.createElement('input');
          input.name = p[0];
          input.value = p[1];
          input.hidden = true;
          input.setAttribute('data-copied', '');
          this.form.append(input);
        }

        try {
          await super.handleSubmit(evt);
        } finally {
          this.form.querySelectorAll('[data-copied], [data-accessory-injected]').forEach((el) => el.remove());
          if (idInput) idInput.disabled = false;
        }
      }

      /**
       * Determine visibility after scroll.
       */
      static handleScroll() {
        const topOffset = document.querySelector('.pageheader--sticky') ? document.querySelector('.section-header').offsetHeight : 0;
        this.classList.toggle(
          'sticky-atc--out',
          this.buyButtons.getBoundingClientRect().bottom > topOffset
        );

        document.body.classList.toggle(
          'scrolled-to-bottom',
          window.scrollY + window.innerHeight + 100 > document.body.scrollHeight
        );
      }

      /**
       * Handle a change in variant on the page.
       * @param {Event} evt - Variant change event dispatched by variant-picker.
       */
      onVariantChange(evt) {
        const idInput = this.form?.querySelector('[name="id"]');

        if (evt.detail.variant) {
          this.variantTitle.textContent = evt.detail.variant.title;
          this.variantContainer.hidden = false;
          if (idInput) idInput.value = evt.detail.variant.id;
        } else {
          this.variantContainer.hidden = true;
          this.variantTitle.textContent = '';
          if (idInput) idInput.value = '';
        }

        this.updateImage();
        this.updateAddToCartButton(evt.detail.variant);
        this.setErrorMsgState();
      }

      /**
       * Updates the image.
       */
      updateImage() {
        const mainImage = this.section.querySelector('.media-gallery .main-image .slider__item.is-active .product-media img');

        if (mainImage) {
          this.imageContainer.innerHTML = mainImage.outerHTML;
        } else {
          this.imageContainer.textContent = '';
        }
      }

      /**
       * Updates the "Add to Cart" button label and status.
       * @param {object} variant - Current variant object.
       */
      updateAddToCartButton(variant) {
        if (!this.submitBtn) return;

        const variantAvailable = variant && variant.available;
        const unavailableStr = this.submitBtn.dataset.unavailableText || 'Unavailable';

        this.submitBtn.textContent = variantAvailable
          ? this.submitBtn.dataset.addToCartText
          : unavailableStr;
      }
    }

    customElements.define('sticky-atc', StickyAtc);
  });
}
